import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { Callout } from '@/features/local-mcp';
import {
  useMcpServersQuery,
  useRefreshMcpServerMutation,
} from '@/features/settings/sub-settings/settings-local-mcp.query';

export function AppConnectionStatus({
  serverSlug,
  appName,
}: {
  serverSlug: string;
  appName: string;
}) {
  const serversQuery = useMcpServersQuery();
  const refresh = useRefreshMcpServerMutation();
  const server = serversQuery.data?.find(
    (candidate) => candidate.slug === serverSlug
  );
  if (!server) return null;

  const lastTest = server.lastConnectionTest;
  const testButton = (
    <Button
      variant="ghost"
      size="sm"
      className="shrink-0"
      isLoading={refresh.isPending}
      onClick={() => refresh.mutate(server)}
    >
      {lastTest ? 'Test again' : 'Test connection'}
    </Button>
  );

  if (lastTest?.ok === false) {
    return (
      <Callout tone="danger" icon={<Icons.warn className="size-4" />}>
        <XStack className="items-center gap-3">
          <span className="min-w-0 flex-1 text-sm font-medium text-foreground">
            Can’t reach {appName}
          </span>
          {testButton}
        </XStack>
      </Callout>
    );
  }

  if (!lastTest) {
    return (
      <XStack className="items-center gap-2.5 rounded-lg border border-border bg-surface px-3 py-2.5">
        <span className="min-w-0 flex-1 text-sm text-foreground">
          Not tested yet
        </span>
        {testButton}
      </XStack>
    );
  }

  return (
    <XStack className="items-center gap-2.5 rounded-lg border border-border bg-surface px-3 py-2.5">
      <span className="flex size-[22px] shrink-0 items-center justify-center rounded-full bg-success-3 text-success-11">
        <Icons.check className="size-3.5" />
      </span>
      <YStack className="min-w-0 flex-1">
        <span className="text-sm text-foreground">Connected to {appName}</span>
        {lastTest.toolCount !== undefined && (
          <span className="text-xs text-foreground-muted">
            {lastTest.toolCount} {lastTest.toolCount === 1 ? 'tool' : 'tools'}{' '}
            available
          </span>
        )}
      </YStack>
      {testButton}
    </XStack>
  );
}
